import React, {useCallback, useEffect, useRef, useState} from "react"
import {FormProvider, useForm} from "react-hook-form"
import {useTranslation} from "react-i18next"
import {toast} from "react-toastify"
import {XMarkIcon} from "@heroicons/react/20/solid"
import {zodResolver} from "@hookform/resolvers/zod"
import isEqual from "fast-deep-equal"
import {z} from "zod"

import {GenericErrorAlert} from "../../../components/Alert.tsx"
import Button, {IconButton} from "../../../components/Button.tsx"
import {CheckboxConnected} from "../../../components/fields/Checkbox.tsx"
import {InputField} from "../../../components/fields/Input.tsx"
import {DropdownConnected} from "../../../components/formElements/Dropdown/Dropdown.tsx"
import {TOption} from "../../../components/formElements/Dropdown/types.ts"
import {Loading} from "../../../components/Loading.tsx"
import Modal from "../../../components/Modal.tsx"
import {useSalesAssistantSettingsListQuery} from "../../../queries/salesAssistant.ts"
import {useSalesAssistantSettingsPartialUpdateMutation} from "../../../queries/salesAssistant.ts"
import requestError from "../../../services/requestError.tsx"
import {ASalesAssistantSalesPersonSettings} from "../../../services/types.generated.ts"
import {validateInternationalPhoneNumber} from "../../../utils/validation.ts"
import {isAxiosValidationError, setFormErrorsFromAxios} from "../../../utils/validation.ts"

const salesPersonSettingsSchema = z.object({
  end_of_day_call_enabled: z.boolean(),
  end_of_day_call_hour: z.number(),
  sms_notifications_enabled: z.boolean(),
  send_morning_email: z.boolean().optional(),
  phone_number: validateInternationalPhoneNumber(),
  additional_phone_numbers: z
    .array(
      z.object({
        id: z.number().optional(),
        phone_number: validateInternationalPhoneNumber(),
        _destroy: z.boolean().optional(),
      })
    )
    .optional(),
})

type TSalesPersonSettings = z.infer<typeof salesPersonSettingsSchema>

type TUseSalesPersonSettingsFormOptions = {
  /**
   * Fill the form from the first loaded settings only, so later refetches do not overwrite drafts. A successful save
   * still reloads the saved settings into the form.
   */
  keepDraftsOnRefetch?: boolean
}

export const useSalesPersonSettingsForm = ({keepDraftsOnRefetch = false}: TUseSalesPersonSettingsFormOptions = {}) => {
  const {data: settings, error, refetch} = useSalesAssistantSettingsListQuery()
  const updateSettingsMutation = useSalesAssistantSettingsPartialUpdateMutation()

  const form = useForm<TSalesPersonSettings>({
    mode: "onTouched",
    resolver: zodResolver(salesPersonSettingsSchema),
  })

  const {getValues, setValue, setError} = form
  const [additionalPhoneCount, setAdditionalPhoneCount] = useState(0)
  const isFilledRef = useRef(false)
  // The values last loaded into or saved from the form. The form is filled with setValue, so formState.isDirty
  // cannot tell whether the user changed anything.
  const baselineRef = useRef<TSalesPersonSettings | null>(null)

  const addAdditionalPhoneNumber = () => {
    const currentCount = additionalPhoneCount
    setAdditionalPhoneCount(currentCount + 1)
    setValue(`additional_phone_numbers.${currentCount}.phone_number`, "")
    setValue(`additional_phone_numbers.${currentCount}._destroy`, false)
  }

  const removeAdditionalPhoneNumber = (index: number) => {
    const currentValues = getValues().additional_phone_numbers || []
    const field = currentValues[index]

    if (field?.id) {
      setValue(`additional_phone_numbers.${index}._destroy`, true)
      setAdditionalPhoneCount(prev => prev - 1)
    } else {
      const newValues = currentValues.filter((_, i) => i !== index)
      setValue("additional_phone_numbers", newValues)
      setAdditionalPhoneCount(newValues.length)
    }
  }

  const fill = useCallback(
    (salesPerson: ASalesAssistantSalesPersonSettings) => {
      setValue("end_of_day_call_enabled", salesPerson.end_of_day_call_enabled)
      setValue("end_of_day_call_hour", salesPerson.end_of_day_call_hour || 16)
      setValue("sms_notifications_enabled", salesPerson.sms_notifications_enabled)
      setValue("send_morning_email", salesPerson.send_morning_email)
      setValue("phone_number", salesPerson.phone_number || "")
      setValue(
        "additional_phone_numbers",
        (salesPerson.additional_phone_numbers || []).map(phone => ({
          id: phone.id,
          phone_number: phone.phone_number || "",
          _destroy: false,
        }))
      )
      setAdditionalPhoneCount((salesPerson.additional_phone_numbers || []).length)
      baselineRef.current = structuredClone(getValues())
    },
    [setValue, getValues]
  )

  useEffect(() => {
    if (settings?.data?.sales_person) {
      if (keepDraftsOnRefetch && isFilledRef.current) {
        return
      }
      isFilledRef.current = true
      fill(settings.data.sales_person)
    }
  }, [settings, fill, keepDraftsOnRefetch])

  // Set when the saved settings could not be reloaded after a successful save: the form may lack the ids the save
  // assigned, so it must not be sent again until they are reloaded.
  const [needsReload, setNeedsReload] = useState(false)

  /** Loads the saved settings into the form, so new phones get their ids and completed removals drop out. */
  const reloadSaved = async () => {
    const result = await refetch()
    const salesPerson = result.data?.data?.sales_person
    if (result.isError || !salesPerson) {
      setNeedsReload(true)
      return
    }
    fill(salesPerson)
    setNeedsReload(false)
  }

  const save = async () => {
    const values = structuredClone(getValues())
    const {
      end_of_day_call_enabled,
      end_of_day_call_hour,
      sms_notifications_enabled,
      send_morning_email,
      phone_number,
      additional_phone_numbers,
    } = values

    const sales_person: TSalesPersonSettings = {
      end_of_day_call_enabled,
      end_of_day_call_hour,
      sms_notifications_enabled,
      phone_number,
      additional_phone_numbers,
      ...(settings?.data?.sales_person?.company_enabled_send_morning_email && {send_morning_email}),
    }

    try {
      await updateSettingsMutation.mutateAsync({
        sales_person,
      })
      baselineRef.current = values
    } catch (error) {
      if (isAxiosValidationError(error)) {
        const salesPersonErrors = error.response.data.errors.sales_person as {
          phone_number?: string[]
          additional_phone_numbers?: Array<{phone_number?: string[]}>
        }

        if (salesPersonErrors?.phone_number?.length) {
          setError("phone_number", {message: salesPersonErrors?.phone_number[0]}, {shouldFocus: true})
          throw error
        }

        if (salesPersonErrors?.additional_phone_numbers?.length) {
          for (let i = 0; i < salesPersonErrors?.additional_phone_numbers.length; i++) {
            const phoneError = salesPersonErrors?.additional_phone_numbers[i]?.phone_number as string[]
            if (phoneError?.length) {
              setError(
                `additional_phone_numbers.${i}.phone_number`,
                {message: phoneError.join(",")},
                {shouldFocus: true}
              )
              throw error
            }
          }
        }

        setFormErrorsFromAxios(error, setError)
      }
      throw error
    }

    // Refetches do not refill a kept draft, so the saved settings are loaded explicitly. A failed reload does not
    // undo the save.
    if (keepDraftsOnRefetch) {
      await reloadSaved()
    }
  }

  /**
   * Validates and saves. Resolves `"saved"` after a successful PATCH, or `"invalid"` when client validation
   * failed (field errors are set and focused, nothing was sent). Rejects with the raw API error; validation
   * errors from the API are already on the fields. Never toasts and never closes anything.
   */
  const submit = async (): Promise<"saved" | "invalid"> => {
    let outcome: "saved" | "invalid" = "invalid"
    await form.handleSubmit(async () => {
      await save()
      outcome = "saved"
    })()
    return outcome
  }

  /**
   * Whether the form differs from the settings it was last filled with or saved; false until they have loaded and
   * while the saved settings need reloading.
   */
  const hasChanges = () => !needsReload && baselineRef.current != null && !isEqual(getValues(), baselineRef.current)

  return {
    form,
    settings: settings?.data?.sales_person,
    isLoading: !settings,
    error,
    refetch,
    submit,
    hasChanges,
    needsReload,
    reloadSaved,
    isSaving: updateSettingsMutation.isPending,
    additionalPhoneCount,
    addAdditionalPhoneNumber,
    removeAdditionalPhoneNumber,
  }
}

type TSalesPersonSettingsFieldsProps = Pick<
  ReturnType<typeof useSalesPersonSettingsForm>,
  "settings" | "additionalPhoneCount" | "addAdditionalPhoneNumber" | "removeAdditionalPhoneNumber"
> & {
  /** Disables the call time dropdown, which a disabled fieldset does not reach because it is not a native control. */
  disabled?: boolean
}

const timeOptions: Array<TOption<number>> = Array.from({length: 24}, (_, hour) => ({
  value: hour,
  title: `${hour.toString().padStart(2, "0")}:00`,
}))

/** Renders the personal Klara settings fields; must be inside the `form`'s `FormProvider`. */
export const SalesPersonSettingsFields: React.FC<TSalesPersonSettingsFieldsProps> = ({
  settings,
  additionalPhoneCount,
  addAdditionalPhoneNumber,
  removeAdditionalPhoneNumber,
  disabled = false,
}) => {
  const {t} = useTranslation()

  const disabledEmailNotification = !settings?.company_enabled_send_morning_email
  const disabledEndOfDayCall = !settings?.company_enabled_end_of_day_call
  const disabledSmsNotifications = !settings?.company_enabled_sms_notifications

  return (
    <>
      <div className={"mb-4 h-px w-full border-t border-cr-blue-light"} />

      <div className={"mb-4 flex items-center justify-between"}>
        <div className={"aria-disabled:[&_p]:text-cr-grey-30"} aria-disabled={disabledEndOfDayCall}>
          <p className={"text-sm font-semibold text-cr-grey-90"}>
            {t("SalesAssistant_Settings_KlaraSettings_EndOfDayCall_Title")}
          </p>
          <p className={"mt-1 text-xs text-cr-grey-50"}>
            {t("SalesAssistant_Settings_KlaraSettings_EndOfDayCall_Description")}
          </p>
        </div>
        <CheckboxConnected name={"end_of_day_call_enabled"} toggle disabled={disabledEndOfDayCall} />
      </div>

      <div className={"mb-4 flex items-center justify-between"}>
        <div className={"aria-disabled:[&_p]:text-cr-grey-30"} aria-disabled={disabledEndOfDayCall}>
          <p className={"text-sm font-semibold text-cr-grey-90"}>
            {t("SalesAssistant_Settings_KlaraSettings_TimeOfCall_Title")}
          </p>
          <p className={"mt-1 text-xs text-cr-grey-50"}>
            {t("SalesAssistant_Settings_KlaraSettings_TimeOfCall_Description")}
          </p>
        </div>
        <DropdownConnected
          optionValueClassName={"w-24"}
          listWidth={"list-auto"}
          name={"end_of_day_call_hour"}
          options={timeOptions}
          disabled={disabledEndOfDayCall || disabled}
        />
      </div>

      <div className={"mb-4 h-px w-full border-t border-cr-blue-light"} />

      <div className={"mb-4 flex items-center justify-between"}>
        <div className={"aria-disabled:[&_p]:text-cr-grey-30"} aria-disabled={disabledSmsNotifications}>
          <p className={"text-sm font-semibold text-cr-grey-90"}>
            {t("SalesAssistant_Settings_KlaraSettings_SmsNotifications_Title")}
          </p>
          <p className={"mt-1 text-xs text-cr-grey-50"}>
            {t("SalesAssistant_Settings_KlaraSettings_SmsNotifications_Description")}
          </p>
        </div>
        <CheckboxConnected name={"sms_notifications_enabled"} toggle disabled={disabledSmsNotifications} />
      </div>

      <div className={"mb-4 flex items-center justify-between"}>
        <div className={"aria-disabled:[&_p]:text-cr-grey-30"} aria-disabled={disabledEmailNotification}>
          <p className={"text-sm font-semibold text-cr-grey-90"}>
            {t("SalesAssistant_Settings_KlaraSettings_EmailNotifications_Title")}
          </p>
          <p className={"mt-1 text-xs text-cr-grey-50"}>
            {t("SalesAssistant_Settings_KlaraSettings_EmailNotifications_Description")}
          </p>
        </div>
        <CheckboxConnected name={"send_morning_email"} toggle disabled={disabledEmailNotification} />
      </div>

      <div className={"mb-4 h-px w-full border-t border-cr-blue-light"} />

      <div className={"flex items-start justify-between"}>
        <div className={"mt-2"}>
          <p className={"text-sm font-semibold text-cr-grey-90"}>
            {t("SalesAssistant_Settings_KlaraSettings_PrimaryPhoneNumber_Title")}
          </p>
          <p className={"mt-1 text-xs text-cr-grey-50"}>
            {t("SalesAssistant_Settings_KlaraSettings_PrimaryPhoneNumber_Description")}
          </p>
        </div>
        <InputField name={"phone_number"} variant={"medium"} label={""} placeholder={""} />
      </div>

      {[...Array(additionalPhoneCount)].map((_, i) => (
        <div key={i} className={"flex items-start justify-between"}>
          <div className={"mt-2"}>
            <p className={"text-sm font-semibold text-cr-grey-90"}>
              {t("SalesAssistant_Settings_KlaraSettings_AdditionalPhoneNumber_Title")}
            </p>
            <p className={"mt-1 text-xs text-cr-grey-50"}>
              {t("SalesAssistant_Settings_KlaraSettings_AdditionalPhoneNumber_Description")}
            </p>
          </div>
          <div className={"flex items-start"}>
            <IconButton size={"lg"} onClick={() => removeAdditionalPhoneNumber(i)} className={"mt-2 mr-2"}>
              <XMarkIcon className={"h-6 w-6 text-cr-red"} />
            </IconButton>
            <InputField
              name={`additional_phone_numbers.${i}.phone_number`}
              variant={"medium"}
              label={""}
              placeholder={""}
            />
          </div>
        </div>
      ))}

      <button
        type={"button"}
        className={"text-sm leading-5 font-semibold text-cr-blue-mid"}
        onClick={addAdditionalPhoneNumber}
      >
        {t("SalesAssistant_Settings_KlaraSettings_AddAdditionalPhoneNumber")}
      </button>
    </>
  )
}

export const SalesPersonSettingsModal: React.FC<{
  isOpen: boolean
  onClose: () => void
}> = ({isOpen, onClose}) => {
  const {t} = useTranslation()
  const settingsForm = useSalesPersonSettingsForm()
  const {form, isLoading, error, refetch, submit} = settingsForm

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submit()
      .then(outcome => {
        if (outcome === "saved") {
          toast.success(t("SalesAssistant_Settings_KlaraSettings_SaveSuccess"))
          onClose()
        }
      })
      .catch((error: unknown) => {
        // Validation errors are already on the fields; anything else gets the generic request toast.
        if (!isAxiosValidationError(error)) {
          requestError(error)
        }
      })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"lg"} title={t("SalesAssistant_Settings_KlaraSettings_Title")}>
      {error ? (
        <GenericErrorAlert retry={refetch} />
      ) : isLoading ? (
        <Loading size={"xl"} />
      ) : (
        <FormProvider {...form}>
          <form onSubmit={handleSubmit}>
            <SalesPersonSettingsFields {...settingsForm} />

            <div className={"mt-4 mb-6 h-px w-full border-t border-cr-blue-light"} />

            <div className={"flex justify-end"}>
              <Button variant={"contained"} color={"blue"} type={"submit"}>
                {t("SalesAssistant_Settings_KlaraSettings_SaveChanges")}
              </Button>
            </div>
          </form>
        </FormProvider>
      )}
    </Modal>
  )
}
